import { useState } from 'react';

import { SentryHelper } from '~/helpers/Sentry/SentryHelper';
import { VinovestNetworkingHelper } from '~/helpers/VinovestNetworkingHelper';
import { useGetDepositPortfolioID } from '~/hooks/useGetDepositPortfolioID';
import { PortfolioTypes } from '~/models/PortfolioType';
import { depositAmountInCents, getDepositAmtWithFeesInCents } from '~/utils/deposits/depositAmountUtils';

interface BitpayReturn {
    error: string;
    loading: boolean;
    openBitpayPopup: (amount: number) => Promise<void>;
    bitpayComplete: boolean;
}

/** Create bitpay invoice and pass to bitpay popup for user to complete their payment. */
export const useBitpayDeposit = (destinationPortfolio: PortfolioTypes): BitpayReturn => {
    const { depositPortfolioID } = useGetDepositPortfolioID(destinationPortfolio);
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [paid, setPaid] = useState(false);
    const [bitpayComplete, setBitpayComplete] = useState(false);

    const openBitpayPopup = async (depositAmount: number) => {
        setLoading(true);
        const depositAmountCents = getDepositAmtWithFeesInCents(depositAmountInCents(depositAmount), 'bitpay', '');

        try {
            const { managed } = VinovestNetworkingHelper.getClient();
            if (depositPortfolioID) {
                const bitpayPayload = {
                    amount: {
                        amount: depositAmountCents,
                        currency: 'USD'
                    },
                    portfolioID: depositPortfolioID,
                    redirect: 'https://vinovest.co/deposit/confirmation'
                };

                const { data: createInvoice, status } = await managed.createBitpayInvoice(bitpayPayload);

                if (status !== 200) {
                    setLoading(false);
                    setError('Unable to initiate this bitpay transaction. Please try again.');
                }

                const { bitpay } = window as { [key: string]: any };

                createInvoice?.invoiceID && bitpay.showInvoice(createInvoice.invoiceID);

                window.addEventListener(
                    'message',
                    (event) => {
                        const paymentStatus = event.data.status;

                        if (paymentStatus === 'paid') {
                            setPaid(true);
                        }
                    },
                    false
                );

                bitpay.onModalWillLeave(() => {
                    setLoading(false);
                    if (paid) {
                        setBitpayComplete(true);
                    }
                });
            }
        } catch (error) {
            SentryHelper.captureException(error);
            setError('Error connecting to Bitpay, please try again later.');
            setLoading(false);
        }
    };
    return { error, loading, openBitpayPopup, bitpayComplete };
};
